import React, { Fragment, useState, useCallback, MouseEvent } from "react";
import { Input, Select, Box } from "@thenounproject/lingo-core";
import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalFooter from "../../../../app/_components/ModalFooter";
import ModalBody from "../../../../app/_components/ModalBody";
import useProductTypes from "@actions/productTypes/useProductTypes";
import useCreateProduct from "@actions/products/useCreateProduct";
import { useSelectSpace } from "@src/@redux/selectors/entities/spaces";

type Props = {
  dismissModal?: (e?: MouseEvent | KeyboardEvent) => void;
  onSuccess?: () => void;
};

const AddProductModal: React.FC<Props> = ({ dismissModal, onSuccess }) => {
  const [name, setName] = useState("");
  const [productTypeUuid, setProductTypeUuid] = useState<string | null>(null);
  const space = useSelectSpace();

  const { data, isLoading: isLoadingTypes } = useProductTypes({ spaceId: space.id });
  const productTypes = React.useMemo(() => data?.productTypes || [], [data]);

  const [createProduct, { isProcessing }] = useCreateProduct();
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleProductTypeChange = useCallback((value: string) => {
    setProductTypeUuid(value);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim() || !productTypeUuid || !data) return;

    const { error } = await createProduct({
      spaceId: space.id,
      productTypeUuid,
      fields: {
        name: { value: name, source: null },
      },
    });

    if (!error) {
      if (onSuccess) onSuccess();
      if (dismissModal) dismissModal();
    }
    // TODO: Show error notification if there's an error
  }, [name, productTypeUuid, data, space.id, createProduct, onSuccess, dismissModal]);

  return (
    <Fragment>
      <ModalHeader title="Add Product" />
      <ModalBody>
        <Box>
          <Input
            label="Name"
            value={name}
            onChange={handleNameChange}
            placeholder="Enter product name"
            autoFocus
          />
        </Box>
        <Box mt="l">
          <Select<string>
            width="100%"
            label="Product type"
            value={productTypeUuid}
            onChange={handleProductTypeChange}
            options={productTypes.map(pt => ({ value: pt.uuid, label: pt.name }))}
            placeholder={isLoadingTypes ? "Loading product types..." : "Select a product type"}
            disabled={isLoadingTypes || productTypes.length === 0}
          />
        </Box>
      </ModalBody>
      <ModalFooter
        primary={{
          text: isProcessing ? "Creating..." : "Create Product",
          onClick: handleSubmit,
          disabled: !name.trim() || !productTypeUuid || isProcessing,
        }}
      />
    </Fragment>
  );
};

export default AddProductModal;
